import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

import {
  getDocFiles,
  uploadDocAttachment,
  updateDocFileList,
} from "../../../api/papers";
import FileTreePicker from "../papers/FileTreePicker";

/**
 * AttachmentBox
 * - dns를 넘기면 자체적으로 파일을 불러와서 보여주는 형태
 */
const AttachmentBox = forwardRef(({ dns, initial=[] }, ref) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (initial.length > 0) {
      setItems(initial);
    }
  }, [initial]);
  const [openLibrary, setOpenLibrary] = useState(false);
  const fileInputRef = useRef(null);

  /* -------------------------------------------------------
   * 1) 기존 문서 첨부파일 자동 로딩 (dns가 있을 때)
   * ----------------------------------------------------- */
  useEffect(() => {
    if (!dns) return;

    (async () => {
      try {
        const res = await getDocFiles(dns);

        // ⭐ 응답을 배열 형태로 변환 (map 오류 방지)
        const files = Array.isArray(res)
          ? res
          : Array.isArray(res?.results)
          ? res.results
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const mapped = files.map((f) => ({
          _type: "library",
          id: f.file_id ?? f.id,
          name: f.name,
        }));

        setItems(mapped);
      } catch (e) {
        console.error("첨부파일 로딩 실패:", e);
      }
    })();
  }, [dns]);

  /* -------------------------------------------------------
   * 2) 저장(save)
   * ----------------------------------------------------- */
  const save = async (dns) => {
    if (!dns) return;

    const finalIds = [];

    for (const it of items) {
      if (it._type === "library") {
        // 기존 파일 → id 그대로
        finalIds.push(it.id);
      } else if (it._type === "local") {
        // 새 파일 업로드 → 업로드 response에서 id 가져오기
        const uploaded = await uploadDocAttachment(dns, {
          file: it.file,
          name: it.name,
        });

        // ★ upload 응답 구조에 따라 맞춰야 함
        const newId = uploaded?.id || uploaded?.file?.id || uploaded?.data?.id;

        if (!newId) {
          console.error("업로드된 파일의 ID를 읽을 수 없습니다:", uploaded);
          continue;
        }

        finalIds.push(newId);
      }
    }

    // 최종 파일 리스트를 서버에 동기화
    await updateDocFileList(dns, finalIds);
  };

  /* -------------------------------------------------------
   * 3) 외부에서 접근 가능한 API 정의
   * ----------------------------------------------------- */
  useImperativeHandle(ref, () => ({
    save,
    getItems: () => items,
    clear: () => setItems([]),
  }));

  /* -------------------------------------------------------
   * 4) 로컬 파일 첨부
   * ----------------------------------------------------- */
  const handleLocalChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const mapped = files.map((f) => ({
      _type: "local",
      file: f,
      name: f.name,
    }));

    setItems((prev) => [...prev, ...mapped]);
    e.target.value = "";
  };

  /* -------------------------------------------------------
   * 5) 자료실 선택
   * ----------------------------------------------------- */
  const handleConfirmLibrary = (files) => {
    const mapped = files.map((f) => ({
      _type: "library",
      id: f.id,
      name: f.name,
      ...f,
    }));

    setItems((prev) => {
      const existing = new Set(
        prev.filter((it) => it._type === "library").map((it) => it.id)
      );

      const newOnes = mapped.filter((f) => !existing.has(f.id));

      return [...prev, ...newOnes];
    });
  };

  /* -------------------------------------------------------
   * 6) UI
   * ----------------------------------------------------- */
  return (
    <div className="space-y-3">
      {/* 첨부 버튼 영역 – 터치 친화적으로 크기 확대 */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-2 text-[13px] font-medium text-slate-700 shadow-sm active:scale-95"
          onClick={() => setOpenLibrary(true)}
        >
          {/* 작은 아이콘 텍스트 – 시각적 구분용 */}
          <span>자료실에서 선택</span>
        </button>

        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-700 shadow-sm active:scale-95"
          onClick={() => fileInputRef.current?.click()}
        >
          <span>파일 첨부</span>
        </button>

        {/* 로컬 파일 선택 인풋 – 화면에는 보이지 않도록 처리 */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleLocalChange} // 로컬 파일 선택 시 상태 갱신
        />
      </div>

      {/* 첨부 목록 */}
      {items.length > 0 && (
        <ul className="border rounded-lg divide-y">
          {items.map((it, i) => (
            <li
              key={`${it._type}-${i}`}
              className="flex items-center justify-between px-3 py-2 text-sm"
            >
              <span className="truncate">
                {it.name}
                {it._type === "local" && (
                  <em className="ml-1 text-xs text-slate-400">(로컬)</em>
                )}
              </span>

              <button
                type="button"
                onClick={() =>
                  setItems((prev) => prev.filter((_, idx) => idx !== i))
                }
                className="text-slate-600 hover:text-red-500"
              >
                삭제
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* 자료실 선택 모달 */}
      <FileTreePicker
        open={openLibrary}
        onClose={() => setOpenLibrary(false)}
        selectionMode="multiple"
        onConfirm={handleConfirmLibrary}
      />
    </div>
  );
});

export default AttachmentBox;
